import React, { useMemo } from 'react';
import {
  BarChart2,
  ClipboardPlus,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/**
 * Per-enrollment row actions — config wrapper around GroupedRowActionSheet.
 * Row-scoped workflows live here; page Actions holds org-wide enrollment tools only.
 */
export default function EnrollmentRowActionsMenu({
  row,
  onEdit,
  onViewScores,
  onAssignAssessment,
  onToggleStatus,
  onDelete,
}) {
  const { lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const title = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.studentEmail;
  const isActive = row.activestatus === 1;

  const groupItems = useMemo(() => [
    {
      key: 'scores',
      icon: <BarChart2 {...ICON_PROPS} />,
      label: 'View in grade center',
      ...lockProps('action.enrollment.viewScores'),
    },
    {
      key: 'assign',
      icon: <ClipboardPlus {...ICON_PROPS} />,
      label: 'Assign assessment',
      ...lockProps('action.enrollment.assignAssessment'),
    },
  ], [lockProps]);

  const items = useMemo(() => [
    {
      key: 'toggle',
      icon: isActive ? <ToggleLeft {...ICON_PROPS} /> : <ToggleRight {...ICON_PROPS} />,
      label: isActive ? 'Set inactive' : 'Set active',
      ...lockProps('action.enrollment.toggleStatus'),
    },
  ], [isActive, lockProps]);

  const handleAction = (key) => {
    if (key === 'edit') onEdit(row);
    else if (key === 'scores') onViewScores(row);
    else if (key === 'assign') onAssignAssessment(row);
    else if (key === 'toggle') onToggleStatus(row);
    else if (key === 'delete') onDelete(row);
  };

  return (
    <>
      <GroupedRowActionSheet
        entityLabel="Learner"
        title={title}
        menuAriaLabel="Learner actions"
        layoutKey={`${row.id}-${isActive}`}
        primary={{
          key: 'edit',
          icon: <Pencil {...ICON_PROPS} />,
          label: 'Edit enrollment',
          ...lockProps('action.enrollment.edit'),
        }}
        groupLabel="For this learner"
        groupItems={groupItems}
        items={items}
        dangerItem={{
          key: 'delete',
          icon: <Trash2 {...ICON_PROPS} />,
          label: 'Delete',
          ...lockProps('action.enrollment.delete'),
        }}
        onAction={handleAction}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
